import { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useMediaQuery, useTheme } from '@ublend-npm/aulaui-core';
import { location as getLocation } from '@app/selectors/routing';
import push from '../utils/routing';

const getQueryParam = (key: string) => {
  if (typeof window === 'undefined') return null;
  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams.has(key)) return searchParams.get(key);

  const hash = window.location.hash;
  if (hash.includes('?')) {
    const hashParams = new URLSearchParams(hash.substring(hash.indexOf('?')));
    return hashParams.get(key);
  }
  return null;
};

export const useResponsiveSpacesNavigation = (
  widgetType?: 'all' | 'starred' | 'recent'
) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'all' | 'starred' | 'recent'>(
    'all'
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const location = useSelector(getLocation);

  useEffect(() => {
    // Catch the parameter when the route changes
    const openModalParam = getQueryParam('openModal');
    if (openModalParam && !isMobile && !isModalOpen) {
      if (
        widgetType &&
        openModalParam !== widgetType &&
        openModalParam !== 'all'
      ) {
        return;
      }
      setModalType(openModalParam as 'all' | 'starred' | 'recent');
      setIsModalOpen(true);
      // We no longer clear the query param here. By keeping it in the URL,
      // the modal state survives widget remounts caused by grid layout repacking.
    }
  }, [isMobile, isModalOpen, widgetType, location]);

  const openSpacesList = useCallback(
    (type: 'all' | 'starred' | 'recent' = 'all') => {
      setModalType(type);
      if (isMobile) {
        push(`/all-spaces?filter=${type}`);
      } else {
        setIsModalOpen(true);
        // Persist to URL so it survives layout remounts
        push(`/home?openModal=${type}`);
      }
    },
    [isMobile]
  );

  const closeAllSpacesModal = useCallback(() => {
    setIsModalOpen(false);
    if (getQueryParam('openModal')) {
      push('/home');
    }
  }, []);

  useEffect(() => {
    if (isMobile && isModalOpen) {
      setIsModalOpen(false);
      push(`/all-spaces?filter=${modalType}`);
    }
  }, [isMobile, isModalOpen, modalType]);

  return {
    isModalOpen,
    modalType,
    openSpacesList,
    closeAllSpacesModal,
  };
};

export default useResponsiveSpacesNavigation;
